import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { LIST_LIMITS } from "@estelle/domain";
import type { Page, Project, UserSummary } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

/**
 * Six users and four projects total (see `fixtures/team.ts` and
 * `fixtures/tasks.ts`) — small enough that a screen fetches the whole
 * directory once and resolves every id it needs against it in memory,
 * the same trade `TransfersList` makes fetching every warehouse to resolve
 * two ids per transfer.
 */
const DIRECTORY_LIMIT = LIST_LIMITS.tasks.max;

/**
 * The active people, for turning an `assigneeUserId` or an actor id into a name
 * and for the `@` picker.
 *
 * Read from the tasks directory (`TasksRepository.listUsers`: ids and names,
 * for anyone who may view tasks), **not** from `settings.listUsers`. That one
 * needs `settings.users.manage`, and every task screen used to go through it —
 * so for anyone without that permission the task drawer, the lists and the
 * forms all failed to load, and a notification's link could not open.
 */
export function useUserDirectory(): UseQueryResult<Page<UserSummary>> {
  const data = useDataSource();
  return useQuery({
    queryKey: ["tasks", "directory", "users"],
    queryFn: () => data.tasks.listUsers(),
  });
}

/** Every project, for turning a `projectId` into its key and name. */
export function useProjectDirectory(): UseQueryResult<Page<Project>> {
  const data = useDataSource();
  return useQuery({
    queryKey: ["tasks", "directory", "projects"],
    queryFn: () => data.tasks.listProjects({ limit: DIRECTORY_LIMIT }),
  });
}

/** What a person who is not in the directory is called: not active, so not listed. */
export const INACTIVE_USER_LABEL = "Inactive user";

/**
 * A user's display name. While the directory is still loading it falls back to
 * the id, rather than a blank, which would read as the person's name actually
 * being empty. Once it has loaded, an id it does not list is someone who is not
 * active — suspended, or invited and not yet signed in, for the directory holds
 * only active people — so it says so instead of showing a number.
 */
export function userLabel(
  users: readonly UserSummary[] | undefined,
  id: string,
): string {
  if (users === undefined) return id;
  return users.find((user) => user.id === id)?.name ?? INACTIVE_USER_LABEL;
}

/** A project's `"OPS — Operations"`, falling back to the id for the same reason. */
export function projectLabel(
  projects: readonly Project[] | undefined,
  id: string,
): string {
  const project = projects?.find((candidate) => candidate.id === id);
  return project ? `${project.key} — ${project.name}` : id;
}
